import VideoDisplay, { VideoTrackInfo } from '@/app/components/VideoDisplay';

import { cn } from '@/lib/utils';

interface PictureInPictureTileProps {
  videoTrack: VideoTrackInfo;
  name?: string;
  isScreenShare?: boolean;
  onSwap(videoTrack: VideoTrackInfo): void;
}

/**
 * A participant waiting off-stage. The whole tile swaps them onto the stage,
 * not just the part of it the video happens to cover: the Agora player only
 * fills the box when its aspect ratio matches, and the name across the bottom
 * lets clicks through, so a tap near an edge used to land on nothing.
 *
 * The press comes from the design system's buttons, which squash to 95% —
 * :active reaches the wrapper from the overlay, so the tile squashes whole.
 *
 * A screen share keeps a phone tile of its own: a 16:9 capture squeezed into
 * the portrait thumbnail is neither readable nor easy to hit.
 */
export default function PictureInPictureTile({ videoTrack, name, isScreenShare = false, onSwap }: PictureInPictureTileProps) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl shadow-lg ring-1 ring-white/20 transition-transform duration-200 ease-in-out active:scale-95 md:aspect-video md:w-full',
        isScreenShare ? 'aspect-video w-40' : 'aspect-portrait w-24',
      )}>
      <VideoDisplay videoTrack={videoTrack} />
      {/* z-10 clears the full-size box the Agora SDK lays over a remote video. */}
      <button
        className="absolute inset-0 z-10 size-full cursor-pointer"
        onClick={() => onSwap(videoTrack)}
        title="Swap with the main view"
        type="button">
        <span className="sr-only">
          {name ? `Show ${name} in the main view` : 'Show in the main view'}
        </span>
      </button>
    </div>
  );
}
